import { useEffect, useState } from "react";
import { Star, MailCheck } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../lib/i18n";

function Stars({ value, size = 16 }) {
  return (
    <div className="inline-flex items-center gap-0.5" data-testid="star-display">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          className={i <= Math.round(value) ? "text-yellow-400" : "text-slate-300"}
          fill={i <= Math.round(value) ? "currentColor" : "none"}
        />
      ))}
    </div>
  );
}

export default function ProductReviews({ slug }) {
  const { t } = useLang();
  const [data, setData] = useState({ reviews: [], average_rating: 0, count: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/products/${slug}/reviews`)
      .then((r) => setData(r.data))
      .catch(() => setData({ reviews: [], average_rating: 0, count: 0 }))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <section className="mt-10" data-testid="product-reviews-section">
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="font-display text-2xl font-bold">{t("reviews.title")}</h2>
        {data.count > 0 && (
          <div className="flex items-center gap-2" data-testid="reviews-summary">
            <Stars value={data.average_rating} />
            <span className="text-sm text-slate-600">
              {t(data.count === 1 ? "reviews.based_on" : "reviews.based_on_plural", { n: data.count })}
            </span>
          </div>
        )}
      </div>

      {!loading && data.reviews.length === 0 && (
        <p className="text-sm text-slate-500 mt-3" data-testid="reviews-empty">{t("reviews.no_reviews")}</p>
      )}

      <div className="mt-4 space-y-3">
        {data.reviews.map((r) => (
          <div key={r.id} className="neo-flat p-4" data-testid={`review-${r.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm">{r.display_name}</span>
                {r.verified_purchase && (
                  <span
                    className="text-[10px] font-bold px-1.5 py-0.5 rounded border-2 border-slate-900"
                    style={{ background: "#A3E635" }}
                    data-testid={`review-verified-${r.id}`}
                  >
                    {t("reviews.verified")}
                  </span>
                )}
              </div>
              <Stars value={r.rating} size={14} />
            </div>
            <p className="text-sm text-slate-600 mt-2">{r.review_text}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-start gap-2 text-sm text-slate-500" data-testid="reviews-purchase-hint">
        <MailCheck size={16} className="mt-0.5 shrink-0" />
        <span>{t("reviews.purchase_hint")}</span>
      </div>
    </section>
  );
}
